import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  CircularProgress,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import { SortOrder } from '../types/table';
import dayjs from 'dayjs';

export interface ServerSideTableColumn<T> {
  id: keyof T;
  label: string;
  width?: string;
  firstCell?: boolean;
  render?: (value: any, row: T) => React.ReactNode;
  sortable?: boolean;
}

export interface ServerSideTableProps<T> {
  data: T[];
  columns: ServerSideTableColumn<T>[];
  loading: boolean;
  page: number;
  rowsPerPage: number;
  totalCount: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
  order: SortOrder;
  orderBy: string;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  onSort: (property: keyof T) => void;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  getRowStyle?: (row: T) => React.CSSProperties | Record<string, any>;
}

export default function ServerSideTable<T extends { id?: string | number }>({
  data,
  columns,
  loading,
  page,
  rowsPerPage,
  totalCount,
  totalPages,
  hasNext,
  hasPrev,
  order,
  orderBy,
  onPageChange,
  onRowsPerPageChange,
  onSort,
  onRowClick,
  emptyMessage = 'No data available',
  getRowStyle,
}: ServerSideTableProps<T>) {
  const handlePageChange = (event: unknown, newPage: number) => {
    onPageChange(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onRowsPerPageChange(parseInt(event.target.value, 10));
  };

  const handleSort = (property: keyof T) => {
    onSort(property);
  };

  const handleRowClick = (row: T) => {
    if (onRowClick) {
      onRowClick(row);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <TableContainer>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={String(column.id)}
                  style={{ width: column.width }}
                  sortDirection={orderBy === column.id ? order : false}
                  sx={{
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    borderBottom: '2px solid',
                    borderBottomColor: 'primary.dark',
                    '& .MuiTableSortLabel-root': {
                      color: 'primary.contrastText',
                      '&:hover': {
                        color: 'primary.contrastText',
                      },
                      '&.Mui-active': {
                        color: 'primary.contrastText',
                        '& .MuiTableSortLabel-icon': {
                          color: 'primary.contrastText',
                        },
                      },
                    },
                  }}
                >
                  {column.sortable !== false ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, index) => {
                const customStyle = getRowStyle ? getRowStyle(row) : {};
                return (
                  <TableRow
                    key={row.id || index}
                    hover={!!onRowClick}
                    onClick={() => handleRowClick(row)}
                    sx={{
                      cursor: onRowClick ? 'pointer' : 'default',
                      backgroundColor:
                        index % 2 === 0 ? 'background.paper' : 'action.hover',
                      '&:hover': onRowClick
                        ? {
                            backgroundColor: 'action.selected',
                            transition: 'background-color 0.2s ease',
                          }
                        : {},
                      transition: 'background-color 0.2s ease',
                      ...customStyle,
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={String(column.id)}
                        sx={{
                          borderBottom: '1px solid',
                          borderBottomColor: 'divider',
                          padding: '12px 16px',
                          fontSize: '0.875rem',
                        }}
                      >
                        {column.render ? (
                          column.render(row[column.id], row)
                        ) : (
                          <Box>
                            {column.id === 'local_chapters' &&
                            Array.isArray(row[column.id]) ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 0.5,
                                }}
                              >
                                {(row[column.id] as string[]).map(
                                  (chapter: string, idx: number) => (
                                    <Chip
                                      key={idx}
                                      label={chapter}
                                      size="small"
                                      variant="outlined"
                                    />
                                  )
                                )}
                              </Box>
                            ) : column.id === 'created_at' ? (
                              dayjs(row[column.id] as string).format(
                                'MM/DD/YYYY'
                              )
                            ) : (
                              String(row[column.id] || '')
                            )}
                          </Box>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handlePageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
        }
        labelRowsPerPage="Rows per page:"
        sx={{
          borderTop: '1px solid',
          borderTopColor: 'divider',
          backgroundColor: 'background.default',
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
            {
              fontSize: '0.875rem',
              fontWeight: 500,
            },
        }}
      />
    </Paper>
  );
}
